import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { toast } from "sonner";
import { ShieldCheck, User as UserIcon, SignOut, Key, Buildings, Image as ImageIcon, Trash, Broom, Info, HardHat, FileText } from "@phosphor-icons/react";
import { APP_VERSION, BUILD_DATE, BUILD_SHA } from "@/version";

export default function Settings() {
  const { user, logout } = useAuth();
  const { companyName, shortName, logoUrl, refresh: refreshBranding } = useBranding();
  const isAdmin = user?.role === "admin";
  const [users, setUsers] = useState([]);

  // Company name form
  const [companyDraft, setCompanyDraft] = useState(companyName);
  const [savingCompany, setSavingCompany] = useState(false);

  // Logo upload
  const logoInputRef = useRef(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  // Change password form
  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  // Orphan-file cleanup (admin)
  const [cleanupScanning, setCleanupScanning] = useState(false);
  const [cleanupDeleting, setCleanupDeleting] = useState(false);
  const [cleanupResult, setCleanupResult] = useState(null);

  // Storage usage (admin)
  const [storage, setStorage] = useState(null);
  const [editingLimit, setEditingLimit] = useState(false);
  const [limitDraft, setLimitDraft] = useState("");
  const [savingLimit, setSavingLimit] = useState(false);

  // Personalliggare (gratis tillval)
  const [pll, setPll] = useState({ enabled: false, id06_site_number: "" });
  const [pllSiteDraft, setPllSiteDraft] = useState("");
  const [savingPll, setSavingPll] = useState(false);

  // ÄTA-hantering (gratis tillval)
  const [ata, setAta] = useState({ enabled: false });
  const [savingAta, setSavingAta] = useState(false);

  const load = async () => {
    if (!isAdmin) return;
    try {
      const [u, s, p, a] = await Promise.all([
        api.get("/users"),
        api.get("/settings/storage-usage").catch(() => ({ data: null })),
        api.get("/personalliggare/settings").catch(() => ({ data: null })),
        api.get("/ata/settings").catch(() => ({ data: null })),
      ]);
      setUsers(u.data);
      if (s.data) setStorage(s.data);
      if (p.data) {
        setPll({
          enabled: !!p.data.enabled,
          id06_site_number: p.data.id06_site_number || "",
        });
        setPllSiteDraft(p.data.id06_site_number || "");
      }
      if (a.data) setAta({ enabled: !!a.data.enabled });
    } catch {
      // silent
    }
  };

  const refreshStorage = async () => {
    try {
      const r = await api.get("/settings/storage-usage");
      setStorage(r.data);
    } catch {
      // silent
    }
  };

  const saveStorageLimit = async () => {
    const v = parseInt(limitDraft, 10);
    if (!Number.isFinite(v) || v < 10 || v > 10240) {
      toast.error("Värdet måste vara mellan 10 och 10240 MB");
      return;
    }
    setSavingLimit(true);
    try {
      const r = await api.patch("/settings/storage-limit", { limit_mb: v });
      setStorage(r.data);
      setEditingLimit(false);
      toast.success(`Lagringsgräns satt till ${v} MB`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte uppdatera gränsen");
    } finally {
      setSavingLimit(false);
    }
  };

  useEffect(() => { load(); }, [isAdmin]);
  useEffect(() => { setCompanyDraft(companyName); }, [companyName]);

  const saveCompanyName = async (e) => {
    e.preventDefault();
    const name = companyDraft.trim();
    if (!name) {
      toast.error("Företagsnamn får inte vara tomt");
      return;
    }
    if (name === companyName) return;
    setSavingCompany(true);
    try {
      await api.patch("/settings/branding", { company_name: name });
      await refreshBranding();
      toast.success("Företagsnamn sparat");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara företagsnamn");
    } finally {
      setSavingCompany(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      toast.error("Endast PNG, JPG, WEBP eller SVG");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Logotypen är för stor (max 4 MB)");
      return;
    }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/settings/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshBranding();
      toast.success("Logotyp uppladdad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ladda upp logotyp");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm("Ta bort företagets logotyp?")) return;
    setRemovingLogo(true);
    try {
      await api.delete("/settings/branding/logo");
      await refreshBranding();
      toast.success("Logotyp borttagen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ta bort logotyp");
    } finally {
      setRemovingLogo(false);
    }
  };

  const admins = users.filter((u) => u.role === "admin");

  const scanOrphans = async () => {
    setCleanupScanning(true);
    setCleanupResult(null);
    try {
      const r = await api.post("/objects/files/cleanup-orphans?dry_run=true");
      setCleanupResult(r.data);
      if (r.data.missing === 0) {
        toast.success("Inga föräldralösa filer hittades");
      } else {
        toast.info(`${r.data.missing} föräldralös${r.data.missing > 1 ? "a" : ""} fil${r.data.missing > 1 ? "er" : ""} hittades`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sökningen misslyckades");
    } finally {
      setCleanupScanning(false);
    }
  };

  const deleteOrphans = async () => {
    if (!cleanupResult || cleanupResult.missing === 0) return;
    const msg = `Detta tar bort ${cleanupResult.missing} fil-rad${cleanupResult.missing > 1 ? "er" : ""} ur databasen. Bytes är redan förlorade och kan inte återskapas. Fortsätt?`;
    if (!window.confirm(msg)) return;
    setCleanupDeleting(true);
    try {
      const r = await api.post("/objects/files/cleanup-orphans");
      toast.success(`Tog bort ${r.data.deleted} fil-rad${r.data.deleted !== 1 ? "er" : ""}`);
      setCleanupResult(null);
      refreshStorage();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rensningen misslyckades");
    } finally {
      setCleanupDeleting(false);
    }
  };


  const demote = async (u) => {
    if (u.user_id === user?.user_id) {
      if (!window.confirm("Du håller på att ta bort din egen admin-roll. Fortsätt?")) return;
    } else {
      if (!window.confirm(`Ta bort admin-rollen för ${u.name}?`)) return;
    }
    try {
      await api.patch(`/users/${u.user_id}`, { role: "employee" });
      toast.success("Admin borttagen");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fel");
    }
  };

  const togglePersonalliggare = async () => {
    setSavingPll(true);
    try {
      const r = await api.patch("/personalliggare/settings", { enabled: !pll.enabled });
      setPll({ enabled: !!r.data.enabled, id06_site_number: r.data.id06_site_number || "" });
      setPllSiteDraft(r.data.id06_site_number || "");
      toast.success(r.data.enabled ? "Personalliggare aktiverad" : "Personalliggare avaktiverad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSavingPll(false);
    }
  };

  const saveSiteNumber = async () => {
    setSavingPll(true);
    try {
      const r = await api.patch("/personalliggare/settings", {
        id06_site_number: pllSiteDraft.trim() || null,
      });
      setPll({ enabled: !!r.data.enabled, id06_site_number: r.data.id06_site_number || "" });
      toast.success("Arbetsplats-ID sparat");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSavingPll(false);
    }
  };

  const toggleAta = async () => {
    setSavingAta(true);
    try {
      const r = await api.patch("/ata/settings", { enabled: !ata.enabled });
      setAta({ enabled: !!r.data.enabled });
      toast.success(r.data.enabled ? "ÄTA-hantering aktiverad" : "ÄTA-hantering avaktiverad");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte spara");
    } finally {
      setSavingAta(false);
    }
  };


  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwd.next.length < 8) {
      toast.error("Nytt lösenord måste vara minst 8 tecken");
      return;
    }
    if (pwd.next !== pwd.confirm) {
      toast.error("Lösenorden matchar inte");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      toast.success("Lösenordet ändrades");
      setPwd({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunde inte ändra lösenord");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="settings-page">
      <header>
        <h1 className="font-heading text-4xl md:text-5xl tracking-tight">Inställningar</h1>
        <p className="text-text-muted mt-1">Hantera din profil och systemet.</p>
      </header>

      {isAdmin && (
        <section className="tk-card p-6" data-testid="company-name-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
            <Buildings size={14} /> Företagsuppgifter
          </div>
          <p className="text-sm text-text-muted mt-2">
            Namnet visas på inloggningssidan, i sidomenyn, i webbläsarfliken och högst upp i PDF-rapporter.
            Det korta namnet i sidomenyn skapas automatiskt från första ordet i versaler.
          </p>
          <form onSubmit={saveCompanyName} className="mt-4 space-y-3 max-w-md">
            <div>
              <label className="text-xs text-text-muted" htmlFor="company-name-input">
                Företagsnamn (max 60 tecken)
              </label>
              <input
                id="company-name-input"
                data-testid="company-name-input"
                type="text"
                required
                maxLength={60}
                value={companyDraft}
                onChange={(e) => setCompanyDraft(e.target.value)}
                className="tk-input w-full"
                placeholder="t.ex. Trappkonsulten AB"
              />
              <div className="mt-2 text-[11px] text-text-muted" data-testid="company-short-preview">
                Kort namn i sidomenyn:{" "}
                <span className="font-mono uppercase">
                  {(companyDraft.trim().split(/\s+/)[0] || companyDraft).toUpperCase() || shortName}
                </span>
              </div>
            </div>
            <button
              type="submit"
              data-testid="save-company-name-button"
              disabled={savingCompany || !companyDraft.trim() || companyDraft.trim() === companyName}
              className="tk-btn-primary"
            >
              {savingCompany ? "Sparar…" : "Spara företagsnamn"}
            </button>
          </form>
        </section>
      )}

      {isAdmin && (
        <section className="tk-card p-6" data-testid="company-logo-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
            <ImageIcon size={14} /> Företagslogotyp
          </div>
          <p className="text-sm text-text-muted mt-2">
            Visas tillsammans med företagsnamnet i sidomenyn, på inloggningssidan och i PDF-rapporter.
            PNG, JPG, WEBP eller SVG. Max 4 MB.
          </p>

          <div className="mt-5 flex items-center gap-5">
            <div
              data-testid="logo-preview"
              className="w-24 h-24 rounded-lg border border-app bg-surface-hover flex items-center justify-center overflow-hidden"
            >
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Företagslogotyp"
                  className="max-w-full max-h-full object-contain"
                  data-testid="logo-preview-image"
                />
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-text-muted">Ingen logotyp</span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <input
                ref={logoInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />
              <button
                type="button"
                data-testid="upload-logo-button"
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="tk-btn-primary inline-flex items-center gap-2"
              >
                <ImageIcon size={16} />
                {uploadingLogo
                  ? "Laddar upp…"
                  : logoUrl
                    ? "Byt logotyp"
                    : "Ladda upp logotyp"}
              </button>
              {logoUrl && (
                <button
                  type="button"
                  data-testid="remove-logo-button"
                  onClick={handleRemoveLogo}
                  disabled={removingLogo}
                  className="tk-btn-outline inline-flex items-center gap-2 text-sm"
                >
                  <Trash size={16} /> {removingLogo ? "Tar bort…" : "Ta bort logotyp"}
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {isAdmin && (
        <section className="tk-card p-6" data-testid="personalliggare-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
            <HardHat size={14} /> Personalliggare för bygg <span className="tk-pill tk-pill-active text-[10px] ml-2">Gratis tillval</span>
          </div>
          <p className="text-sm text-text-muted mt-2">
            Digital personalliggare för byggarbetsplatser (Skatteförfarandelagen 39 kap 11–12 §§).
            När den är aktiverad kan du fylla i personnummer och ID06-nummer på anställda, lägga till
            underentreprenörer per objekt och exportera personalliggaren som PDF. Modulen är helt frivillig.
          </p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-sm">
              Status:{" "}
              <span
                className={`tk-pill ${pll.enabled ? "tk-pill-active" : ""}`}
                data-testid="personalliggare-status"
              >
                {pll.enabled ? "Aktiverad" : "Inaktiverad"}
              </span>
            </div>
            <button
              data-testid="toggle-personalliggare-button"
              onClick={togglePersonalliggare}
              disabled={savingPll}
              className={pll.enabled ? "tk-btn-outline" : "tk-btn-primary"}
            >
              {savingPll ? "Sparar…" : pll.enabled ? "Avaktivera modulen" : "Aktivera modulen"}
            </button>
          </div>

          {pll.enabled && (
            <div className="mt-5 pt-5 border-t border-app space-y-3 max-w-md" data-testid="personalliggare-config">
              <div>
                <label className="text-xs text-text-muted">
                  ID06 arbetsplats-ID (valfritt — visas på PDF-exporten)
                </label>
                <input
                  data-testid="id06-site-number-input"
                  type="text"
                  maxLength={40}
                  placeholder="t.ex. 1234-5678"
                  value={pllSiteDraft}
                  onChange={(e) => setPllSiteDraft(e.target.value)}
                  className="tk-input w-full font-mono"
                />
              </div>
              <button
                data-testid="save-id06-site-number-button"
                onClick={saveSiteNumber}
                disabled={savingPll || pllSiteDraft.trim() === (pll.id06_site_number || "")}
                className="tk-btn-primary"
              >
                {savingPll ? "Sparar…" : "Spara arbetsplats-ID"}
              </button>
              <p className="text-xs text-text-muted">
                Nästa steg: gå till <strong>Anställda</strong> för att sätta personnummer per anställd, och till varje <strong>Objekt</strong> för att öppna personalliggaren och lägga till underentreprenörer.
              </p>
            </div>
          )}
        </section>
      )}


      <section className="tk-card p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
          <UserIcon size={14} /> Min profil
        </div>
        <div className="mt-4 flex items-center gap-4">
          {user?.picture ? (
            <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-full bg-surface-hover flex items-center justify-center text-2xl text-text-muted">
              {user?.name?.[0] || "?"}
            </div>
          )}
          <div>
            <div className="font-heading text-xl" data-testid="settings-user-name">{user?.name}</div>
            <div className="text-sm text-text-muted">{user?.email}</div>
            <div className="mt-1">
              <span className={`tk-pill ${user?.role === "admin" ? "tk-pill-warning" : ""}`}>
                {user?.role === "admin" ? "Admin" : "Anställd"}
              </span>
            </div>
          </div>
        </div>
        <button
          data-testid="settings-logout-button"
          onClick={logout}
          className="tk-btn-outline mt-6 inline-flex items-center gap-2"
        >
          <SignOut size={16} /> Logga ut
        </button>
      </section>

      {isAdmin && (
        <section className="tk-card p-6" data-testid="ata-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
            <FileText size={14} /> ÄTA-hantering <span className="tk-pill tk-pill-active text-[10px] ml-2">Gratis tillval</span>
          </div>
          <p className="text-sm text-text-muted mt-2">
            Digital hantering av Ändrings- och tilläggsarbeten (ÄTA) enligt AB 04 / ABT 06. När den är
            aktiverad kan du skapa ÄTA-poster per objekt, exportera A4-PDF med QR-kod och skicka
            beställaren en signeringslänk via mejl (godkänner eller nekar digitalt med IP + tidsstämpel).
          </p>
          <div className="mt-4 flex items-center justify-between gap-3">
            <div className="text-sm">
              Status:{" "}
              <span
                className={`tk-pill ${ata.enabled ? "tk-pill-active" : ""}`}
                data-testid="ata-status"
              >
                {ata.enabled ? "Aktiverad" : "Inaktiverad"}
              </span>
            </div>
            <button
              data-testid="toggle-ata-button"
              onClick={toggleAta}
              disabled={savingAta}
              className={ata.enabled ? "tk-btn-outline" : "tk-btn-primary"}
            >
              {savingAta ? "Sparar…" : ata.enabled ? "Avaktivera modulen" : "Aktivera modulen"}
            </button>
          </div>
          {ata.enabled && (
            <p className="text-xs text-text-muted mt-3">
              Nästa steg: gå till ett <strong>Objekt</strong> och öppna ÄTA-fliken för att skapa din första ÄTA-post.
            </p>
          )}
        </section>
      )}


      <section className="tk-card p-6" data-testid="change-password-section">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
          <Key size={14} /> Ändra lösenord
        </div>
        <p className="text-sm text-text-muted mt-2">
          Välj ett nytt lösenord på minst 8 tecken. Du kommer fortsätta vara inloggad efteråt.
        </p>
        <form onSubmit={handleChangePassword} className="mt-4 space-y-3 max-w-md">
          <div>
            <label className="text-xs text-text-muted">Nuvarande lösenord</label>
            <input
              data-testid="current-password-input"
              type="password"
              required
              autoComplete="current-password"
              value={pwd.current}
              onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
              className="tk-input w-full"
            />
          </div>
          <div>
            <label className="text-xs text-text-muted">Nytt lösenord (minst 8 tecken)</label>
            <input
              data-testid="new-password-input"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={pwd.next}
              onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
              className="tk-input w-full"
            />
          </div>
          <div>
            <label className="text-xs text-text-muted">Bekräfta nytt lösenord</label>
            <input
              data-testid="confirm-password-input"
              type="password"
              required
              minLength={8}
              autoComplete="new-password"
              value={pwd.confirm}
              onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
              className="tk-input w-full"
            />
          </div>
          <button
            type="submit"
            data-testid="change-password-submit-button"
            disabled={saving || !pwd.current || !pwd.next || !pwd.confirm}
            className="tk-btn-primary"
          >
            {saving ? "Sparar…" : "Spara lösenord"}
          </button>
        </form>
      </section>

      {isAdmin && (
        <section className="tk-card p-6" data-testid="admins-list-section">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
            <ShieldCheck size={14} /> Administratörer
          </div>
          <p className="text-sm text-text-muted mt-2">
            Användare med admin-behörighet kan se allas rapporter, hantera objekt och moment.
            Skapa en ny admin via <span className="font-medium text-text-primary">Anställda → Lägg till användare</span>,
            eller uppgradera en befintlig anställd med "Gör admin".
          </p>

          <div className="mt-4 divide-y divide-[var(--border-c)]">
            {admins.length === 0 ? (
              <p className="text-sm text-text-muted py-4">Inga admins registrerade.</p>
            ) : (
              admins.map((a) => (
                <div
                  key={a.user_id}
                  data-testid={`admin-row-${a.user_id}`}
                  className="py-3 flex items-center justify-between"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-surface-hover flex items-center justify-center text-xs">
                      {a.name?.[0] || "?"}
                    </div>
                    <div>
                      <div className="font-heading text-sm">{a.name}</div>
                      <div className="text-xs text-text-muted">{a.email}</div>
                    </div>
                  </div>
                  <button
                    data-testid={`demote-admin-${a.user_id}`}
                    onClick={() => demote(a)}
                    className="tk-btn-outline !py-1.5 !px-3 text-xs"
                  >
                    Ta bort admin
                  </button>
                </div>
              ))
            )}
          </div>
        </section>
      )}

      {isAdmin && storage && (
        <section className="tk-card p-6" data-testid="storage-usage-section">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <h2 className="font-heading text-xl tracking-tight">Lagringsutrymme</h2>
              <p className="text-sm text-text-muted mt-1">
                Filer (PDF, Excel, DXF) lagrade i MongoDB GridFS.
              </p>
            </div>
            {!editingLimit && (
              <button
                data-testid="edit-storage-limit-button"
                onClick={() => { setLimitDraft(String(storage.limit_mb)); setEditingLimit(true); }}
                className="tk-btn-outline text-sm"
              >
                Ändra gräns
              </button>
            )}
          </div>

          <div className="space-y-3" data-testid="storage-usage-display">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-heading tracking-tight">
                {formatMB(storage.bytes_used)} av {storage.limit_mb} MB
              </span>
              <span
                className={`text-sm font-medium ${
                  storage.percent >= 90 ? "text-red-500"
                  : storage.percent >= 70 ? "text-amber-500"
                  : "text-text-muted"
                }`}
                data-testid="storage-percent"
              >
                {storage.percent}%
              </span>
            </div>

            <div className="h-3 w-full rounded-full bg-surface-hover overflow-hidden">
              <div
                data-testid="storage-progress-bar"
                className={`h-full transition-all ${
                  storage.percent >= 90 ? "bg-red-500"
                  : storage.percent >= 70 ? "bg-amber-500"
                  : "bg-[var(--brand-forest)]"
                }`}
                style={{ width: `${Math.min(100, storage.percent)}%` }}
              />
            </div>

            <div className="flex items-center justify-between text-xs text-text-muted">
              <span>{storage.file_count} fil{storage.file_count === 1 ? "" : "er"} totalt</span>
              {storage.percent >= 70 && (
                <span className="text-amber-500">
                  Tips: arkivera färdiga objekt för att frigöra utrymme
                </span>
              )}
            </div>
          </div>

          {editingLimit && (
            <div className="mt-5 pt-5 border-t border-app flex items-end gap-2 flex-wrap" data-testid="storage-limit-editor">
              <div className="flex-1 min-w-[140px]">
                <label className="text-xs uppercase tracking-wider text-text-muted">
                  Ny gräns (MB)
                </label>
                <input
                  data-testid="storage-limit-input"
                  type="number"
                  min={10}
                  max={10240}
                  value={limitDraft}
                  onChange={(e) => setLimitDraft(e.target.value)}
                  className="tk-input mt-1 w-full"
                />
              </div>
              <button
                data-testid="save-storage-limit-button"
                onClick={saveStorageLimit}
                disabled={savingLimit}
                className="tk-btn"
              >
                {savingLimit ? "Sparar…" : "Spara"}
              </button>
              <button
                data-testid="cancel-storage-limit-button"
                onClick={() => setEditingLimit(false)}
                className="tk-btn-outline"
              >
                Avbryt
              </button>
            </div>
          )}
        </section>
      )}

      {isAdmin && (
        <section className="tk-card p-6" data-testid="orphan-cleanup-section">
          <div className="flex items-start gap-3 mb-3">
            <Broom size={22} className="text-[var(--brand-rust)] mt-0.5" />
            <div>
              <h2 className="font-heading text-xl tracking-tight">Rensa föräldralösa filer</h2>
              <p className="text-sm text-text-muted mt-1">
                Hittar och tar bort fil-rader i databasen vars faktiska innehåll saknas
                (t.ex. äldre filer som försvann innan permanent lagring infördes).
                Bytes är redan borta — detta städar bara upp listan så du slipper se
                meddelandet &ldquo;Filen finns inte längre i lagringen&rdquo; på övergivna rader.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <button
              data-testid="scan-orphans-button"
              onClick={scanOrphans}
              disabled={cleanupScanning || cleanupDeleting}
              className="tk-btn-outline inline-flex items-center gap-2"
            >
              {cleanupScanning ? "Söker…" : "Sök föräldralösa"}
            </button>
            {cleanupResult && cleanupResult.missing > 0 && (
              <button
                data-testid="delete-orphans-button"
                onClick={deleteOrphans}
                disabled={cleanupDeleting}
                className="tk-btn inline-flex items-center gap-2"
              >
                <Trash size={16} />
                {cleanupDeleting ? "Tar bort…" : `Ta bort ${cleanupResult.missing}`}
              </button>
            )}
          </div>

          {cleanupResult && (
            <div className="mt-4 rounded-lg border border-app bg-surface p-4 text-sm" data-testid="cleanup-result">
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-text-muted">
                <span>Skannade: <strong className="text-text-primary">{cleanupResult.scanned}</strong></span>
                <span>Saknas: <strong className="text-text-primary">{cleanupResult.missing}</strong></span>
              </div>
              {cleanupResult.items && cleanupResult.items.length > 0 && (
                <ul className="mt-3 space-y-1 max-h-48 overflow-auto">
                  {cleanupResult.items.map((it) => (
                    <li key={it.file_id} className="text-xs text-text-muted truncate">
                      <span className="text-text-primary">{it.filename || it.file_id}</span>
                      <span className="text-text-muted"> · {it.object_id}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </section>
      )}

      <section className="tk-card p-6" data-testid="about-app-section">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-muted">
          <Info size={14} /> Om appen
        </div>
        <div className="mt-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="font-heading text-3xl tracking-tight" data-testid="about-app-version">
              Version {APP_VERSION}
            </div>
            <div className="text-sm text-text-muted mt-1">
              Byggd {BUILD_DATE}
            </div>
          </div>
          <div
            className="text-xs font-mono text-text-muted"
            data-testid="about-app-build"
            title="Commit-hash för denna build"
          >
            Build: {BUILD_SHA}
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-app flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <p className="text-sm text-text-muted">
            Se alla nyheter, förbättringar och fixar mellan versioner.
          </p>
          <a
            href="/versionslogg"
            data-testid="about-changelog-link"
            className="tk-btn-outline !py-2 !px-4 text-sm inline-flex items-center gap-2 self-start sm:self-auto"
          >
            Visa versionslogg
          </a>
        </div>
        <p className="text-sm text-text-muted mt-4">
          Har du hittat ett fel eller vill föreslå en förbättring? Kontakta support så återkommer vi snarast.
        </p>
      </section>
    </div>
  );
}

function formatMB(bytes) {
  if (!bytes || bytes < 0) return "0 MB";
  const mb = bytes / (1024 * 1024);
  if (mb < 0.1) return `${(bytes / 1024).toFixed(0)} KB`;
  if (mb < 10) return `${mb.toFixed(1)} MB`;
  return `${Math.round(mb)} MB`;
}
